import { deserializeXYWH } from '@blocksuite/global/gfx';
import type { SurfaceBlockProps } from '@blocksuite/std/gfx';
import { GfxPrimitiveElementModel } from '@blocksuite/std/gfx';
import {
  SURFACE_TEXT_UNIQ_IDENTIFIER,
  SURFACE_YMAP_UNIQ_IDENTIFIER,
} from '@blocksuite/std/gfx';
import type {
  FromSnapshotPayload,
  PageElementInput,
  SnapshotNode,
  ToSnapshotPayload,
} from '@blocksuite/store';
import { BaseBlockTransformer } from '@blocksuite/store';
import * as Y from 'yjs';

// Snapshots keep a group's members as its children map, as before.
const GROUP_ELEMENT_TYPE = 'group';
const GROUP_MEMBERS_KEY = 'children';

/** A snapshot map field as a record, wrapped as a surface Y.Map or plain. */
export function snapshotMapRecord(value: unknown): Record<string, unknown> {
  const record =
    value instanceof Object && Reflect.has(value, SURFACE_YMAP_UNIQ_IDENTIFIER)
      ? Reflect.get(value, 'json')
      : value;
  return record instanceof Object ? (record as Record<string, unknown>) : {};
}

/** The keys of a snapshot map field, wrapped as a surface Y.Map or a plain record. */
export function snapshotMapKeys(value: unknown): string[] {
  return Object.keys(snapshotMapRecord(value));
}

export class SurfaceBlockTransformer extends BaseBlockTransformer<SurfaceBlockProps> {
  private *_elementToJSONWork(
    element: Y.Map<unknown>
  ): Generator<void, Record<string, unknown>> {
    const value: Record<string, unknown> = {};
    for (const [key, child] of element.entries()) {
      yield;
      if (child instanceof Y.Text) {
        value[key] = {
          [SURFACE_TEXT_UNIQ_IDENTIFIER]: true,
          delta: child.toDelta(),
        };
      } else if (child instanceof Y.Map) {
        value[key] = {
          [SURFACE_YMAP_UNIQ_IDENTIFIER]: true,
          json: yield* GfxPrimitiveElementModel.serializeValueWork(child),
        };
      } else value[key] = child;
    }
    return value;
  }

  private _fromJSON(value: unknown): unknown {
    if (value instanceof Object) {
      if (Reflect.has(value, SURFACE_TEXT_UNIQ_IDENTIFIER)) {
        const yText = new Y.Text();
        yText.applyDelta(Reflect.get(value, 'delta'));
        return yText;
      } else if (Reflect.has(value, SURFACE_YMAP_UNIQ_IDENTIFIER)) {
        const yMap = new Y.Map();
        const json = Reflect.get(value, 'json') as Record<string, unknown>;
        Object.entries(json).forEach(([key, value]) => {
          yMap.set(key, value);
        });
        return yMap;
      }
    }
    return value;
  }

  elementFromJSON(element: Record<string, unknown>) {
    const yMap = new Y.Map();
    Object.entries(element).forEach(([key, value]) => {
      yMap.set(key, this._fromJSON(value));
    });

    return yMap;
  }

  /**
   * A snapshot element as the store places it: the layer index becomes the
   * order of its structure entry and a group's children the g of its
   * members, so neither stays a field. A mind map keeps its node map.
   */
  elementInput(id: string, json: Record<string, unknown>): PageElementInput {
    const { index, ...fields } = json;
    let members: string[] = [];
    if (fields.type === GROUP_ELEMENT_TYPE) {
      members = snapshotMapKeys(fields[GROUP_MEMBERS_KEY]);
      delete fields[GROUP_MEMBERS_KEY];
    }
    return {
      id,
      fields: this.elementFromJSON(fields),
      rect: deserializeXYWH(json.xywh),
      order: typeof index === 'string' ? index : null,
      members,
    };
  }

  override async fromSnapshot(
    payload: FromSnapshotPayload
  ): Promise<SnapshotNode<SurfaceBlockProps>> {
    const snapshotRet = await super.fromSnapshot(payload);
    const elementsJSON = (snapshotRet.props as Record<string, unknown>)
      .elements as Record<string, Record<string, unknown>> | undefined;
    const elements = Object.entries(elementsJSON ?? {}).map(([id, json]) =>
      this.elementInput(id, json)
    );
    snapshotRet.props = { elements } as unknown as SurfaceBlockProps;
    return snapshotRet;
  }

  override *toSnapshotWork(payload: ToSnapshotPayload<SurfaceBlockProps>) {
    const snapshot = yield* super.toSnapshotWork(payload);
    const { model } = payload;
    const structure = model.store.pageStructure;
    const value: Record<string, unknown> = {};
    const selectedElements = this.transformerConfigs.get('selectedElements') as
      | Set<string>
      | undefined;
    for (const id of structure.tree().elements.get(model.id) ?? []) {
      yield;
      const element = structure.elementFields(id);
      if (!element || (selectedElements && !selectedElements.has(id))) {
        continue;
      }
      const json = yield* this._elementToJSONWork(element);
      json.index = structure.entry(id)!.o;
      if (json.type === GROUP_ELEMENT_TYPE) {
        json[GROUP_MEMBERS_KEY] = {
          [SURFACE_YMAP_UNIQ_IDENTIFIER]: true,
          json: Object.fromEntries(
            structure.members(id).map(member => [member, true])
          ),
        };
      }
      value[id] = json;
    }
    snapshot.props = { elements: value };
    return snapshot;
  }

  override toSnapshot(payload: ToSnapshotPayload<SurfaceBlockProps>) {
    const work = this.toSnapshotWork(payload);
    let next = work.next();
    while (!next.done) next = work.next();
    return next.value;
  }
}
